<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';

// Número-manchete de um produto. A variação é um dado secundário: ela ganha um
// ícone de direção além da cor, porque cor sozinha não é legível para quem não
// distingue verde de vermelho.
const props = defineProps({
  label: { type: String, required: true },
  value: { type: [Number, String], default: null },
  hint: { type: String, default: '' },
  icon: { type: String, default: '' },
  // Classe estática vinda do pai: o Tailwind só gera o que consegue ler no
  // fonte, então a cor não pode ser montada em runtime.
  iconClass: { type: String, default: 'text-n-slate-11' },
  delta: { type: Number, default: null },
});

const nf = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

const displayValue = computed(() =>
  typeof props.value === 'number'
    ? nf.format(props.value)
    : (props.value ?? '—')
);

const hasDelta = computed(
  () => props.delta !== null && Number.isFinite(props.delta)
);
const isUp = computed(() => props.delta > 0);
const deltaLabel = computed(() => {
  const rounded = Math.abs(Math.round(props.delta));
  return `${isUp.value ? '+' : '−'}${rounded}%`;
});
</script>

<template>
  <div
    class="flex flex-col gap-2 p-4 rounded-pz border border-n-weak bg-n-card shadow-pz"
  >
    <div class="flex gap-2 items-center min-w-0">
      <span
        v-if="icon"
        class="grid flex-shrink-0 place-content-center rounded-md size-6 bg-n-alpha-1"
        :class="iconClass"
      >
        <Icon :icon="icon" class="size-3.5" />
      </span>
      <span class="text-xs font-medium truncate text-n-slate-11">
        {{ label }}
      </span>
    </div>

    <div class="flex gap-2 items-baseline">
      <span class="text-2xl font-semibold tabular-nums text-n-slate-12">
        {{ displayValue }}
      </span>
      <span
        v-if="hasDelta"
        class="flex gap-0.5 items-center text-xs font-medium tabular-nums"
        :class="isUp ? 'text-n-teal-11' : 'text-n-ruby-11'"
      >
        <Icon
          :icon="isUp ? 'i-lucide-trending-up' : 'i-lucide-trending-down'"
          class="size-3"
        />
        {{ deltaLabel }}
      </span>
    </div>

    <span v-if="hint" class="text-xs text-n-slate-10">{{ hint }}</span>
  </div>
</template>
